import ExifReader from 'exifreader'
import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import type { BannerProps, FilesListDataItem } from '@fourthwall/components'
import { FilesList, Toasts, VisuallyHiddenInput, useModalContext } from '@fourthwall/components'
import { OffersApiClient } from '@fourthwall/services/dist/OffersApiClient'
import { formatBytes } from '@fourthwall/utils/dist/file'
import {
  SubscriptionDigitalStorageLimitModal,
  useSubscriptionStorageLimits,
} from '@modules/Dashboard/modules/Subscriptions'
import { useMutation } from '@tanstack/react-query'
import { getRejectionErrors } from '@utils/dropzone'
import { routing } from '@utils/routing'

export type ProductFilesProps = {
  offerId: string
  maxSize?: number
  files: FilesListDataItem[]
  isTotalDigitalFilesSizeExceeded?: boolean
  isFreeProduct?: boolean
  withMembersOnly?: boolean
  isFormEditable: boolean
}

const supportedExifTypes = [
  'image/jpeg',
  'image/tiff',
  'image/png',
  'image/heic',
  'image/webp',
  'image/gif',
]

const LearnMoreLink = () => (
  <a href={routing.external.helpCenter.digitalFilesSizeLimits} target="_blank" rel="noreferrer">
    Learn more
  </a>
)

export const ProductFiles = ({
  offerId,
  files,
  maxSize = 2147483648,
  isTotalDigitalFilesSizeExceeded,
  isFreeProduct,
  withMembersOnly,
  isFormEditable,
}: ProductFilesProps) => {
  const { setFieldValue } = useFormikContext()
  const { open } = useModalContext()
  const [uploadedFiles, setUploadedFiles] = useState<{ id: string; name: string; url: string }[]>(
    [],
  )
  const [rejectedFileId, setRejectedFileId] = useState<string | null>(null)
  const maxSizeInBytes = formatBytes(maxSize, { decimals: 0 })
  const shouldShowFreeProductInfo = isFreeProduct && !withMembersOnly
  const { digitalProductsStorage } = useSubscriptionStorageLimits()

  const uploadFileMutation = useMutation({
    mutationFn: async ({ offerId, file }: { offerId: string; file: File }) => {
      const result = await OffersApiClient.uploadFile(offerId, file)

      if (!('data' in result)) {
        throw new Error('Failed to upload file')
      }

      return result.data
    },
  })

  const handleFileDownload = async (file: FilesListDataItem) => {
    if (!file.url) return

    const result = await OffersApiClient.getDownloadFileLink(offerId, file.url)

    if (result.data.uri) {
      window.open(result.data.uri, '_blank')
    }
  }

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      setFieldValue(
        'digitalItems',
        files.map((item) => {
          const uploadedFile = uploadedFiles.find((file) => file.id === item.id)

          if (uploadedFile) {
            return { ...item, isUploading: false, url: uploadedFile.url }
          }

          return item
        }),
      )
    }
  }, [uploadedFiles])

  useEffect(() => {
    if (rejectedFileId) {
      setFieldValue(
        'digitalItems',
        files
          .map((item) => (item.id === rejectedFileId ? null : item))
          .filter((item) => item !== null),
      )
    }
  }, [rejectedFileId])

  const uploadFile = async ({ id, offerId, file }: { id: string; offerId: string; file: File }) => {
    await uploadFileMutation.mutateAsync(
      { offerId, file },
      {
        onSuccess: (data) => {
          setUploadedFiles((state) => [...state, { id, name: file.name, url: data.fileUrl }])
        },
        onError: (error) => {
          setRejectedFileId(id)

          if (error instanceof Error) {
            Toasts.notify(error.message, { type: 'error' })
          }
        },
      },
    )
  }

  const getHasLocationMetadata = async (file: File) => {
    if (!supportedExifTypes.includes(file.type)) {
      return false
    }

    const metadataTags = await ExifReader.load(file)

    return Object.keys(metadataTags).some((tag) => tag.includes('GPS'))
  }

  const handleFileDrop = async (acceptedFiles?: File[]) => {
    if (acceptedFiles && acceptedFiles[0] && offerId) {
      const file = acceptedFiles[0]
      const tmpImageId = uuidv4()
      const hasLocationMetadata = await getHasLocationMetadata(file)
      const size = formatBytes(file.size, { unit: 'decimal' })
      const canAddFile = digitalProductsStorage.canAddFile(file.size)

      if (!canAddFile) {
        open(SubscriptionDigitalStorageLimitModal)

        return
      }

      const value = [
        ...files,
        {
          id: tmpImageId,
          name: file.name,
          size,
          isUploading: true,
          hasLocationMetadata,
        },
      ]

      setFieldValue('digitalItems', value)
      uploadFile({ id: tmpImageId, offerId, file })
    }
  }

  const handleFileRemove = (file: FilesListDataItem) => {
    const value = files.filter((digitalItem) => digitalItem.id !== file.id)

    setFieldValue('digitalItems', value)
  }

  const handleDropRejection = (rejections: any) => {
    getRejectionErrors(rejections).forEach((rejection) => {
      Toasts.notify(rejection, { toastId: rejection })
    })
  }

  const getBanner = (): BannerProps | undefined => {
    if (isTotalDigitalFilesSizeExceeded) {
      return {
        children: (
          <>
            You’ve exceeded file size limit.{' '}
            {shouldShowFreeProductInfo ? 'Free digital ' : 'Digital '}
            products have a {maxSizeInBytes} total file size limit. <LearnMoreLink />
          </>
        ),
        appearance: 'danger',
      }
    }

    if (shouldShowFreeProductInfo) {
      return {
        children: (
          <>
            Free digital products have a {maxSizeInBytes} total file size limit. <LearnMoreLink />
          </>
        ),
      }
    }
  }

  return (
    <>
      {/* Hidden field that will receive the validation error and provide a scroll to correct section */}
      <VisuallyHiddenInput name="digitalItems" />
      <FilesList
        banner={getBanner()}
        maxSize={maxSize}
        data={files}
        disabled={!isFormEditable}
        onFileDrop={handleFileDrop}
        onFileDropRejected={handleDropRejection}
        onFileRemove={handleFileRemove}
        onFileDownload={handleFileDownload}
      />
    </>
  )
}
